import { useEffect, useState } from "react";
import { Mic, MicOff, Clock } from "lucide-react";
import api from "../../api";

// Tells the doctor, on the consultation page itself, what the patient chose
// about transcribing this video session - and what that means for their
// notes. Re-checked every 30s: the patient can change their mind mid-call.
// Same source as the call window's badge (GET /appointments/<id>/recording-consent).
export default function TranscriptionConsentNotice({ appointmentId, patientName }) {
  const [state, setState] = useState(null); // null (unknown) | "granted" | "declined" | "pending"

  useEffect(() => {
    if (!appointmentId) return undefined;
    let cancelled = false;
    const load = () => api.getRecordingConsent(appointmentId)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.recording_allowed) setState("granted");
        else setState(res.data?.consent?.decision === "declined" ? "declined" : "pending");
      })
      .catch(() => { if (!cancelled) setState(null); });
    load();
    const timer = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [appointmentId]);

  if (!state) return null;
  const who = patientName || "The patient";

  if (state === "declined") {
    return (
      <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" role="status">
        <MicOff className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900">
          <p className="text-sm font-bold">Transcription is off for this session</p>
          <p className="mt-0.5">
            <strong>{who}</strong> chose not to have this session transcribed, so there won't be a transcript or
            AI-drafted notes from the conversation. Please write your clinical notes here yourself. AI tools that work
            from your typed notes, like <em>Generate with AI</em> for prescriptions, still work.
          </p>
        </div>
      </div>
    );
  }
  if (state === "pending") {
    return (
      <div className="mb-4 flex items-start gap-3 rounded-xl border border-secondary-200 bg-secondary-50 p-3" role="status">
        <Clock className="w-4 h-4 text-secondary-500 shrink-0 mt-0.5" />
        <p className="text-xs text-secondary-700">
          <strong>Transcription:</strong> waiting for {who}'s choice. They're asked before joining the call, and nothing
          is transcribed unless they agree.
        </p>
      </div>
    );
  }
  return (
    <div className="mb-4 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2" role="status">
      <Mic className="w-4 h-4 text-green-700 shrink-0" />
      <p className="text-xs text-green-800"><strong>Transcription allowed:</strong> {who} agreed to this session being transcribed.</p>
    </div>
  );
}
